/**
 * CTRL_05 Dashboard - Training Data Audit Trail
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Spin, Empty, Progress, Alert } from 'antd';
import { CheckCircleOutlined, WarningOutlined, DatabaseOutlined, SafetyOutlined, StarOutlined } from '@ant-design/icons';
import { getControlResults, getControlEvents } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';

const CONSENT_STATUS_COLORS = {
  'explicit': '#52c41a',
  'implicit': '#1890ff',
  'legitimate_interest': '#722ed1',
  'not_required': '#13c2c2',
  'pending': '#faad14',
  'missing': '#f5222d'
};

function CTRL_05_Dashboard({ projectId }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [events, setEvents] = useState([]);
  const controlCode = 'CTRL_05';

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resultsRes, eventsRes] = await Promise.all([
        getControlResults(projectId),
        getControlEvents(projectId, controlCode).catch(() => ({ data: { events: [] } }))
      ]);

      const controlResults = (resultsRes.data.results || []).find(r => r.control_code === controlCode);
      setResults(controlResults);
      setEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const datasetManifestCoverage = useMemo(() => {
    if (events.length === 0) return { total: 0, tracked: 0, percentage: 0 };
    const tracked = events.filter(e => {
      const metadata = e.event_metadata || {};
      return metadata.dataset_id || metadata.training_data_source;
    }).length;
    return { total: events.length, tracked, percentage: Math.round((tracked / events.length) * 100) };
  }, [events]);

  const consentTracking = useMemo(() => {
    const withConsent = events.filter(e => e.event_metadata?.consent_status).length;
    const distribution = {};
    events.forEach(e => {
      const status = e.event_metadata?.consent_status;
      if (status) {
        distribution[status] = (distribution[status] || 0) + 1;
      }
    });
    return {
      total: events.length,
      withConsent,
      percentage: events.length > 0 ? Math.round((withConsent / events.length) * 100) : 0,
      distribution: Object.entries(distribution).map(([status, count]) => ({
        status,
        count,
        percentage: events.length > 0 ? Math.round((count / events.length) * 100) : 0,
        color: CONSENT_STATUS_COLORS[status] || '#888'
      }))
    };
  }, [events]);

  const qualityScores = useMemo(() => {
    const scores = events
      .map(e => e.event_metadata?.data_quality_score)
      .filter(score => score !== undefined && score !== null);
    if (scores.length === 0) return { average: 0, count: 0 };
    const average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
    return { average: average.toFixed(1), count: scores.length };
  }, [events]);

  const ropaCompliance = useMemo(() => {
    const withRopa = events.filter(e => e.event_metadata?.ropa_documented === true).length;
    return {
      withRopa,
      percentage: events.length > 0 ? Math.round((withRopa / events.length) * 100) : 0
    };
  }, [events]);

  const violations = useMemo(() => {
    return events.filter(e => {
      const metadata = e.event_metadata || {};
      return !metadata.dataset_id && !metadata.training_data_source;
    });
  }, [events]);

  const columns = [
    { title: 'Timestamp', dataIndex: 'ts', key: 'ts', render: (ts) => new Date(ts).toLocaleString(), width: 180 },
    { title: 'Model', dataIndex: 'model', key: 'model', ellipsis: true },
    {
      title: 'Dataset ID',
      dataIndex: ['event_metadata', 'dataset_id'],
      key: 'dataset',
      ellipsis: true,
      render: (id) => id ? <Tag color="blue">{id}</Tag> : <Tag color="red">Missing</Tag>
    },
    {
      title: 'Consent Status',
      dataIndex: ['event_metadata', 'consent_status'],
      key: 'consent',
      render: (status) => status ?
        <Tag color={CONSENT_STATUS_COLORS[status] || '#888'}>{status}</Tag> :
        <Tag color={CONSENT_STATUS_COLORS.missing}>Missing</Tag>
    },
    {
      title: 'Quality Score',
      dataIndex: ['event_metadata', 'data_quality_score'],
      key: 'quality',
      align: 'center',
      render: (score) => score !== undefined ? `${score}` : '-'
    },
    {
      title: 'ROPA',
      key: 'ropa',
      align: 'center',
      render: (_, record) => {
        const ropa = record.event_metadata?.ropa_documented === true;
        return ropa ?
          <Tag color="green" icon={<CheckCircleOutlined />}>Yes</Tag> :
          <Tag color="orange">No</Tag>;
      }
    }
  ];

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Spin size="large" /></div>;
  }

  if (!projectId) {
    return <Empty description="Please select a project to view dashboard" style={{ padding: '40px' }} />;
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2>CTRL_05: Training Data Audit Trail Dashboard</h2>

      {violations.length > 0 && (
        <Alert
          message={`${violations.length} training events missing audit trail`}
          description="Some training runs lack dataset manifest or source documentation."
          type="warning"
          showIcon
          style={{ marginBottom: '24px' }}
          icon={<WarningOutlined />}
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Dataset Manifest Coverage"
              value={datasetManifestCoverage.percentage}
              suffix="%"
              prefix={datasetManifestCoverage.percentage >= 90 ? <CheckCircleOutlined /> : <WarningOutlined />}
              valueStyle={{ color: datasetManifestCoverage.percentage >= 90 ? '#52c41a' : '#faad14' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {datasetManifestCoverage.tracked} of {datasetManifestCoverage.total} tracked
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Consent Tracking"
              value={consentTracking.percentage}
              suffix="%"
              prefix={<SafetyOutlined />}
              valueStyle={{ color: consentTracking.percentage >= 80 ? '#52c41a' : '#faad14' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {consentTracking.withConsent} with consent status
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Avg Quality Score"
              value={qualityScores.average}
              prefix={<StarOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {qualityScores.count} datasets scored
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="ROPA Compliance"
              value={ropaCompliance.percentage}
              suffix="%"
              prefix={<DatabaseOutlined />}
              valueStyle={{ color: ropaCompliance.percentage >= 70 ? '#52c41a' : '#faad14' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {ropaCompliance.withRopa} documented
            </div>
          </Card>
        </Col>
      </Row>

      <Card title="Consent Status Distribution" style={{ marginBottom: '24px' }}>
        <Row gutter={16}>
          {consentTracking.distribution.map((item, idx) => (
            <Col xs={24} sm={12} md={8} key={idx} style={{ marginBottom: '16px' }}>
              <Card size="small" style={{ backgroundColor: `${item.color}15`, borderColor: item.color }}>
                <Statistic
                  title={item.status}
                  value={item.count}
                  suffix={`(${item.percentage}%)`}
                  valueStyle={{ color: item.color }}
                />
                <Progress percent={item.percentage} strokeColor={item.color} size="small" style={{ marginTop: '8px' }} />
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      <Card title="Recent Training Runs">
        <Table
          dataSource={events.slice(0, 20)}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `Total ${total} training events` }}
          size="small"
        />
      </Card>
    </div>
  );
}

export default CTRL_05_Dashboard;
